import {
  APINotificationInterface,
  NotificationInterface,
  UpdateNotificationRequestInterface
} from '@partnerconnect-monorepo/types/notification/Notification';
import { ErrorResponse } from '@partnerconnect-monorepo/types/ErrorResponse';
import { axios } from '../core/AxiosInterceptor/AxiosInterceptor';
import { mapToCamelCase, mapToSnakeCase } from '../helpers/helpers';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { mapFrequencyToOptionElement } from '../utils/notifications';

const getNotifications = (moduleUrl: string) =>
  axios
    .get(`${moduleUrl}/notifications/`)
    .then<Array<APINotificationInterface>>((apiNotifications) =>
      mapToCamelCase(apiNotifications.data)
    )
    .then<Array<NotificationInterface>>((notifications) =>
      notifications.map(({ frequency, allowedFrequencies, ...otherProps }) => ({
        ...otherProps,
        frequency: mapFrequencyToOptionElement(frequency),
        allowedFrequencies: allowedFrequencies.map((frequency) =>
          mapFrequencyToOptionElement(frequency)
        )
      }))
    );

export const useReportingModuleNotifications = (
  options: Partial<
    UseQueryOptions<
      Array<NotificationInterface>,
      AxiosError<ErrorResponse>,
      Array<NotificationInterface>,
      [string]
    >
  > = {}
) =>
  useQuery({
    queryKey: ['REPORTING_MODULE_NOTIFICATIONS'],
    queryFn: () =>
      getNotifications(import.meta.env.VITE_REPORTING_APP_BE_BASE ?? ''),
    ...options
  });

export const useFlaModuleNotifications = (
  options: Partial<
    UseQueryOptions<
      Array<NotificationInterface>,
      AxiosError<ErrorResponse>,
      Array<NotificationInterface>,
      [string]
    >
  > = {}
) =>
  useQuery({
    queryKey: ['PARTNERSHIP_MODULE_NOTIFICATIONS'],
    queryFn: () => getNotifications(import.meta.env.VITE_FLA_APP_BE_BASE ?? ''),
    ...options
  });

const updateNotification = ({
  module,
  code,
  ...payloadProps
}: UpdateNotificationRequestInterface) =>
  axios
    .put(
      `${
        module === 'reporting'
          ? import.meta.env.VITE_REPORTING_APP_BE_BASE
          : import.meta.env.VITE_FLA_APP_BE_BASE
      }/notifications/${code}/`,
      mapToSnakeCase(payloadProps)
    )
    .then<APINotificationInterface>((apiNotification) =>
      mapToCamelCase(apiNotification.data)
    )
    .then<NotificationInterface>(
      ({ frequency, allowedFrequencies, ...otherProps }) => ({
        ...otherProps,
        frequency: mapFrequencyToOptionElement(frequency),
        allowedFrequencies: allowedFrequencies.map((frequency) =>
          mapFrequencyToOptionElement(frequency)
        )
      })
    );

export const useUpdateNotification = (
  options: UseMutationOptions<
    NotificationInterface,
    AxiosError<ErrorResponse>,
    UpdateNotificationRequestInterface
  > = {}
) =>
  useMutation({
    mutationFn: updateNotification,
    ...options
  });
